import { apiDelete, apiFetch, apiPatch, apiPost, apiPut, getApiBase } from '../api/httpClient';
import { getAuthToken } from '../lib/authToken';
import type {
  Department,
  DepartmentTask,
  LedgerEntry,
  PortalBoard,
  WorkOrder,
  WorkOrderAuditEntry,
  WorkOrderDetail,
  WorkOrderInput,
  ZenBootstrap,
  ZenUser,
} from './types';

export const fetchBootstrap = (signal?: AbortSignal) =>
  apiFetch<ZenBootstrap>('/zen/bootstrap', { signal });

export const fetchAuditLog = (signal?: AbortSignal) =>
  apiFetch<WorkOrderAuditEntry[]>('/zen/audit', { signal });

export const fetchWorkOrder = (id: string, signal?: AbortSignal) =>
  apiFetch<WorkOrderDetail>(`/zen/work-orders/${id}`, { signal });

export const createWorkOrder = (input: WorkOrderInput) =>
  apiPost<WorkOrder>('/zen/work-orders', input);

export const updateWorkOrder = (id: string, input: WorkOrderInput) =>
  apiPatch<WorkOrder>(`/zen/work-orders/${id}`, input);

export const cancelWorkOrder = (id: string, reason: string) =>
  apiPost<{ ok: true }>(`/zen/work-orders/${id}/cancel`, { reason });

export const deleteWorkOrder = (id: string) =>
  apiDelete<{ ok: true }>(`/zen/work-orders/${id}`);

export type TaskActionName = 'start' | 'finish' | 'admin-release' | 'admin-complete' | 'admin-reopen';

export const taskAction = (id: string, action: TaskActionName) =>
  apiPost<DepartmentTask>(`/zen/tasks/${id}/${action}`, {});

export const addComment = (id: string, text: string) =>
  apiPost(`/zen/work-orders/${id}/comments`, { text });

export async function uploadFile(workOrderId: string, file: File): Promise<void> {
  const signed = await apiPost<{ fileId: string; uploadUrl: string }>(
    `/zen/work-orders/${workOrderId}/files/presign`,
    { filename: file.name, contentType: file.type || 'application/octet-stream', size: file.size },
  );
  const response = await fetch(signed.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!response.ok) throw new Error('File upload failed');
  await apiPost(`/zen/work-orders/${workOrderId}/files/confirm`, {
    fileId: signed.fileId,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
  });
}

export const fileUrl = (workOrderId: string, fileId: string) =>
  `${getApiBase()}/zen/work-orders/${workOrderId}/files/${fileId}`;

export async function downloadFile(workOrderId: string, fileId: string, filename: string): Promise<void> {
  const response = await fetch(fileUrl(workOrderId, fileId), {
    headers: { Authorization: `Bearer ${getAuthToken() ?? ''}` },
  });
  if (!response.ok) throw new Error('Could not download file');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const saveQuote = (
  id: string,
  quote: { amount: number; notes?: string },
) => apiPut(`/zen/work-orders/${id}/quote`, quote);

export const saveInvoice = (id: string, invoice: { number: string; amount: number; date: string }) =>
  apiPut(`/zen/work-orders/${id}/invoice`, invoice);

export const fetchLedger = (clientId: string, signal?: AbortSignal) =>
  apiFetch<{ balance: number; entries: LedgerEntry[] }>(`/zen/clients/${clientId}/ledger`, { signal });

export const addLedgerEntry = (input: {
  clientId: string;
  workOrderId?: string;
  type: 'opening_balance' | 'payment' | 'adjustment';
  amount: number;
  date: string;
  reference?: string;
  notes?: string;
}) => apiPost<LedgerEntry>('/zen/ledger', input);

export const createDepartment = (input: Partial<Department> & { name: string }) =>
  apiPost<Department>('/zen/departments', input);

export const updateDepartment = (id: string, input: Partial<Department> & { name: string }) =>
  apiPatch<Department>(`/zen/departments/${id}`, input);

export const deleteDepartment = (id: string) =>
  apiDelete<{ ok: true }>(`/zen/departments/${id}`);

export const saveUser = (input: {
  id?: string;
  username: string;
  name: string;
  isAdmin: boolean;
  departmentIds: string[];
  active?: boolean;
  password?: string;
}) => apiPost<ZenUser>('/zen/users', input);

export const deleteUser = (id: string) =>
  apiDelete<{ ok: true }>(`/zen/users/${id}`);

export type AdminResetDomain = 'work-orders' | 'clients' | 'users' | 'departments' | 'activity';

export const resetAdminData = (
  domain: AdminResetDomain,
  input: { confirmation: string; password: string },
) => apiPost<{ ok: true }>(`/zen/admin/reset/${domain}`, input);

const PORTAL_SESSION_KEY = 'ua-portal-session';

export async function fetchPortalDepartments(): Promise<Array<{ id: string; name: string }>> {
  const response = await fetch(`${getApiBase()}/zen/portal/departments`);
  if (!response.ok) throw new Error('Could not load departments');
  return response.json() as Promise<Array<{ id: string; name: string }>>;
}

export async function portalLogin(username: string, password: string, departmentIds: string[]): Promise<void> {
  const response = await fetch(`${getApiBase()}/zen/portal/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, departmentIds }),
  });
  const body = await response.json() as { token?: string; error?: { message?: string } };
  if (!response.ok || !body.token) throw new Error(body.error?.message ?? 'Could not open the view-only portal');
  sessionStorage.setItem(PORTAL_SESSION_KEY, body.token);
}

export function clearPortalSession(): void {
  sessionStorage.removeItem(PORTAL_SESSION_KEY);
}

export async function fetchPortalBoard(signal?: AbortSignal): Promise<PortalBoard> {
  const token = sessionStorage.getItem(PORTAL_SESSION_KEY);
  if (!token) throw new Error('Portal session required');
  const response = await fetch(`${getApiBase()}/zen/portal/board`, {
    signal,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    if (response.status === 401) clearPortalSession();
    throw new Error('View-only session expired');
  }
  return response.json() as Promise<PortalBoard>;
}

export function hasPortalSession(): boolean {
  return Boolean(sessionStorage.getItem(PORTAL_SESSION_KEY));
}
